import { useState, useEffect } from "react";

const API = "https://archpoint.onrender.com/api/team";

export default function AdminTeam() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [notification, setNotification] = useState(null);

  const [form, setForm] = useState({
    name: "",
    role: "",
    bio: "",
    image: null,
  });

  const [imagePreview, setImagePreview] = useState(null);

  /* ================= LOAD TEAMS ================= */
  const load = async () => {
    try {
      const res = await fetch(API, { cache: "no-store" });
      const data = await res.json();
      setTeams(data.data);
    } catch {
      showNotification("Failed to load team members", "error");
    }
  };

  useEffect(() => {
    load();
  }, []);

  /* ================= NOTIFICATION ================= */
  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  /* ================= IMAGE ================= */
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm({ ...form, image: file });
    setImagePreview(URL.createObjectURL(file));
  };

  /* ================= SUBMIT ================= */
  const submit = async () => {
    if (!form.name || !form.role) {
      showNotification("Name and role are required", "error");
      return;
    }

    if (!editId && !form.image) {
      showNotification("Image is required", "error");
      return;
    }

    setLoading(true);

    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("role", form.role);
    fd.append("bio", form.bio || "");
    if (form.image) fd.append("image", form.image);

    try {
      const res = await fetch(editId ? `${API}/${editId}` : API, {
        method: "POST",
        body: fd,
      });

      if (!res.ok) throw new Error("Failed");

      showNotification(editId ? "Member updated!" : "Member added!");

      setEditId(null);
      setForm({ name: "", role: "", bio: "", image: null });
      setImagePreview(null);
      load();
    } catch {
      showNotification("Operation failed", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ================= DELETE ================= */
  const remove = async (id) => {
    try {
      await fetch(`${API}/${id}`, { method: "DELETE" });
      showNotification("Team member deleted");
      load();
    } catch {
      showNotification("Delete failed", "error");
    }
    setDeleteConfirm(null);
  };

  /* ================= REORDER ================= */
  const move = async (index, dir) => {
    const updated = [...teams];
    const target = index + dir;
    if (target < 0 || target >= updated.length) return;

    [updated[index], updated[target]] = [updated[target], updated[index]];
    setTeams(updated);

    await fetch(`${API}/reorder/all`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        orders: updated.map((t, i) => ({
          id: t._id,
          order: i,
        })),
      }),
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-black to-slate-900 text-white p-6">
      {/* NOTIFICATION */}
      {notification && (
        <div
          className={`fixed top-6 right-6 px-6 py-3 rounded-lg shadow-lg backdrop-blur
          ${
            notification.type === "error"
              ? "bg-red-500/90"
              : "bg-emerald-500/90"
          }`}
        >
          {notification.message}
        </div>
      )}

      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8">
        {/* FORM */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-xl">
          <h2 className="text-2xl font-semibold mb-6 text-yellow-400">
            {editId ? "Edit Team Member" : "Add Team Member"}
          </h2>

          <div className="space-y-4">
            <input
              className="w-full px-4 py-3 rounded-lg bg-black/60 border border-white/10 focus:border-yellow-400 outline-none"
              placeholder="Full Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />

            <input
              className="w-full px-4 py-3 rounded-lg bg-black/60 border border-white/10 focus:border-yellow-400 outline-none"
              placeholder="Role / Position"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            />

            <textarea
              className="w-full px-4 py-3 rounded-lg bg-black/60 border border-white/10 focus:border-yellow-400 outline-none resize-none h-28"
              placeholder="Short bio"
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
            />

            <label className="block">
              <span className="text-sm text-gray-300">Profile Image</span>
              <input
                type="file"
                onChange={handleImageChange}
                className="mt-2 block w-full text-sm text-gray-400
                file:mr-4 file:py-2 file:px-4
                file:rounded-lg file:border-0
                file:bg-yellow-400 file:text-black
                hover:file:bg-yellow-500"
              />
            </label>

            {imagePreview && (
              <img
                src={imagePreview}
                className="w-24 h-24 rounded-full object-cover border-2 border-yellow-400"
              />
            )}

            <button
              onClick={submit}
              disabled={loading}
              className="w-full py-3 rounded-lg bg-yellow-400 text-black font-semibold hover:bg-yellow-500 transition disabled:opacity-50"
            >
              {loading ? "Saving..." : editId ? "Update Member" : "Add Member"}
            </button>
          </div>
        </div>

        {/* LIST */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-xl">
          <h2 className="text-2xl font-semibold mb-6 text-yellow-400">
            Team Members
          </h2>

          <div className="space-y-3">
            {teams.map((t, i) => (
              <div
                key={t._id}
                className="flex items-center justify-between p-4 rounded-xl bg-black/60 border border-white/10 hover:border-yellow-400 transition"
              >
                <div>
                  <p className="font-semibold">{t.name}</p>
                  <p className="text-sm text-gray-400">{t.role}</p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => move(i, -1)}
                    className="px-2 py-1 rounded bg-white/10 hover:bg-yellow-400 hover:text-black"
                  >
                    ⬆
                  </button>
                  <button
                    onClick={() => move(i, 1)}
                    className="px-2 py-1 rounded bg-white/10 hover:bg-yellow-400 hover:text-black"
                  >
                    ⬇
                  </button>
                  <button
                    onClick={() => {
                      setEditId(t._id);
                      setForm({
                        name: t.name,
                        role: t.role,
                        bio: t.bio,
                        image: null,
                      });
                      setImagePreview(t.image?.url);
                    }}
                    className="px-2 py-1 rounded bg-blue-500/20 hover:bg-blue-500"
                  >
                    ✏
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(t._id)}
                    className="px-2 py-1 rounded bg-red-500/20 hover:bg-red-500"
                  >
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* DELETE MODAL */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur flex items-center justify-center">
          <div className="bg-slate-900 border border-white/10 p-6 rounded-2xl w-80 text-center">
            <p className="mb-6 text-lg">Delete this member?</p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => remove(deleteConfirm)}
                className="px-4 py-2 rounded bg-red-500 hover:bg-red-600"
              >
                Yes
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 rounded bg-white/10 hover:bg-white/20"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
